import { useState } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../api";
import { useApi } from "../hooks";
import { ErrorState, LoadingState, StatusBadge } from "./Ui";
import { useMutation } from "./Workflow";

type Channel = { id: string; name: string; is_private: boolean; is_member: boolean; client_id: number | null; client_name: string | null };
type Setup = { scopes_known: boolean; missing_public: string[] | null; missing_private: string[] | null; names_available: boolean | null; join_available: boolean | null };
type Channels = { configured: boolean; channels: Channel[]; setup?: Setup; truncated?: boolean; mapped?: { slack_channel_id: string; slack_channel_name: string; client_id: number }[] };
export type IngestionStatus = {
  slack: { owner: string; token_configured: boolean; channels_watched: number; last_checked: string | null; last_result: Record<string, unknown> | null };
  gmail: { owner: string; authorised: boolean; account_email: string | null; last_checked: string | null; last_result: Record<string, unknown> | null };
};

/** One line on the last run, from the checkpoint's last_result. */
export function lastRun(checked: string | null, result: Record<string, unknown> | null, read: string) {
  if (!checked || !result) return "Not run yet.";
  if (typeof result.error === "string") return `Last try ${formatDate(checked)} failed (${result.error}). It retries on the next run.`;
  if (result.skipped) return `Last checked ${formatDate(checked)}: skipped (${String(result.reason || result.skipped).replaceAll("_", " ")}).`;
  const n = Number(result[read] || 0), made = Number(result.tasks_created || 0);
  const failures = Array.isArray(result.channel_errors) ? result.channel_errors as { channel?: string; error?: string; operation?: string }[] : [];
  const issues = failures.map(e => `#${e.channel || "channel"}: ${e.operation || "history"} ${e.error || "unavailable"}`).join("; ");
  return `Last checked ${formatDate(checked)}: ${n} read, ${made} ${made === 1 ? "task" : "tasks"} made.${result.partial ? " Incomplete; unread messages retry on the next run." : ""}${issues ? ` ${issues}.` : ""}`;
}

/** Client settings: the Slack channels whose messages become this client's work queue tasks. */
export default function SlackChannels({ clientId, churned = false }: { clientId: number; churned?: boolean }) {
  const status = useApi<IngestionStatus>("/api/ingestion/status");
  const channels = useApi<Channels>("/api/ingestion/slack/channels");
  const mutation = useMutation();
  const [picked, setPicked] = useState("");

  const all = channels.data?.channels || [];
  const mine = all.filter((c) => c.client_id === clientId);
  const hidden = (channels.data?.mapped || []).filter(c => c.client_id === clientId && !all.some(v => v.id === c.slack_channel_id));
  const setup = channels.data?.setup;
  const choice = all.find((c) => c.id === picked);
  const slack = status.data?.slack;
  // The badge says what is really true for this client: no bot token, no channel
  // of its own, not scheduled on this deployment, or actually running.
  const connected = Boolean(slack?.token_configured) && channels.data?.configured !== false;
  const badge = !slack || (!channels.data && !channels.error) ? null
    : !connected ? <StatusBadge>Not connected</StatusBadge>
    : channels.data && mine.length === 0 ? <StatusBadge>No channels yet</StatusBadge>
    // Churned clients are skipped by the reader, so a watched channel there makes no tasks.
    : churned ? <StatusBadge tone="warning">Paused: client is churned</StatusBadge>
    : slack.owner !== "vercel" ? <StatusBadge tone="warning">Not running here yet</StatusBadge>
    : <StatusBadge tone="success">On, every 15 minutes</StatusBadge>;

  async function watch() {
    if (!choice) return;
    const r = await mutation.run(`/api/ingestion/slack/channels/${choice.id}`, { client_id: clientId, name: choice.name }, "PUT");
    if (r) { setPicked(""); mutation.setNotice(`#${choice.name} added. New messages there become tasks for this client.`); channels.reload(); status.reload(); }
  }
  async function join(c: Channel) {
    if (c.is_private || !window.confirm(`Allow NOLA to join public channel #${c.name} and read its messages and thread replies for this client?`)) return;
    const r = await mutation.run(`/api/ingestion/slack/channels/${c.id}/join`, { client_id: clientId, confirm_join: true });
    if (r) { mutation.setNotice(`NOLA is a member of #${c.name}.`); channels.reload(); status.reload(); }
  }
  async function unwatch(c: Channel) {
    const r = await mutation.run(`/api/ingestion/slack/channels/${c.id}`, {}, "DELETE");
    if (r) { mutation.setNotice(`#${c.name} is no longer watched.`); channels.reload(); status.reload(); }
  }

  return (
    <section className="panel workflow-panel slack-channels">
      <div className="panel-heading">
        <div>
          <h2>Slack channels</h2>
        </div>
        {badge}
      </div>
      <p className="client-settings-hint">
        NOLA reads new messages and thread replies in these selected channels and turns requests and to-dos into work queue tasks for this client.
        Bot posts and alerts are ignored.
      </p>
      {churned && (
        <p className="inline-notice warning" role="status">
          This client is marked Churned, so NOLA does not read its Slack channels and makes no tasks from them.
          If they are still a client, set their status back to Active in Clients.
        </p>
      )}
      {slack && connected && slack.owner !== "vercel" && (
        <p className="client-settings-hint">
          A superadmin turns this on once in <Link to="/settings#security">Settings, System &amp; security</Link>, Scheduled job ownership: set Slack ingestion to This Vercel deployment.
        </p>
      )}
      {channels.error ? <ErrorState message={channels.error} onRetry={channels.reload} />
        : !channels.data ? <LoadingState label="Loading Slack channels" />
        : !channels.data.configured ? (
          <p className="client-settings-hint">Slack isn't connected yet. Add the Slack bot token in <Link to="/settings#integrations">Settings, Integrations</Link> first.</p>
        ) : (
          <>
            {setup && <p className="client-settings-hint">
              Public listing and reading need <code>channels:read</code> and <code>channels:history</code>.
              Private channels also need <code>groups:read</code> and <code>groups:history</code>, plus a manual invite.
              <code> users:read</code> enables sender names; IDs work without it. <code>channels:join</code> is optional for the explicit public join button.
              {!setup.scopes_known ? " Slack did not report granted scopes; API errors will identify missing access."
                : [...(setup.missing_public || []), ...(setup.missing_private || [])].length > 0
                  ? ` Missing grants: ${[...(setup.missing_public || []), ...(setup.missing_private || [])].join(", ")}. Ask the Slack administrator to approve only the channel types you need.` : " Reading scopes are present."}
            </p>}
            {channels.data.truncated && <p className="inline-notice danger" role="alert">Slack channel listing reached its page limit. Some channels may be missing; existing mappings are retained.</p>}
            {hidden.map(c => <p key={c.slack_channel_id} className="inline-notice danger" role="alert">#{c.slack_channel_name} is still mapped but Slack did not return it. Check scopes and membership; private channels need a manual invite.</p>)}
            {mine.length ? (
              <ul className="slack-channel-list">
                {mine.map((c) => (
                  <li key={c.id}>
                    <span><strong>#{c.name}</strong>{!c.is_member && <em> · the bot needs channel membership</em>}</span>
                    {!c.is_member && !c.is_private && <button className="button secondary" disabled={mutation.busy || setup?.join_available === false} onClick={() => void join(c)}>Join public channel</button>}
                    {!c.is_member && c.is_private && <em>Invite the bot in Slack</em>}
                    <button className="text-button" disabled={mutation.busy} onClick={() => void unwatch(c)}>Stop watching</button>
                  </li>
                ))}
              </ul>
            ) : <p className="client-settings-hint">No channels watched for this client yet.</p>}
            <div className="slack-channel-add">
              <label className="select-control">
                <span className="sr-only">Slack channel</span>
                <select value={picked} disabled={mutation.busy} onChange={(e) => setPicked(e.target.value)}>
                  <option value="">Choose a channel</option>
                  {all.filter((c) => c.client_id !== clientId).map((c) => (
                    <option key={c.id} value={c.id} disabled={(c.is_private && !c.is_member) || Boolean(c.client_id)}>
                      #{c.name}{!c.is_member ? c.is_private ? " · invite the bot first" : " · map, then join or invite" : c.client_id ? ` · feeds ${c.client_name || "another client"}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <button className="button secondary" disabled={!choice || mutation.busy} onClick={() => void watch()}>Watch channel</button>
            </div>
            <p className="client-settings-hint">
              Map a selected public channel, then approve its Join public channel action or invite the bot in Slack.
              Mapping alone never joins a channel. Private channels stay invite-only: use <code>/invite @your-bot</code>.
              Replies on previously discovered parent messages are checked in later runs; older unseen history is not backfilled.
            </p>
          </>
        )}
      {slack && connected && <p className="client-settings-hint">{lastRun(slack.last_checked, slack.last_result, "messages_processed")}</p>}
      {mutation.feedback}
    </section>
  );
}
